import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Component from '../../components/Component';
import { BASE_URL } from '../../components/AppConfig';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Link } from 'react-router-dom';

const PRESET_OPTIONS = [
  { label: 'All Time', value: 'all' },
  { label: 'Last 7 Days', value: 'last_7_days' },
  { label: 'Last 30 Days', value: 'last_30_days' },
  { label: 'This Month', value: 'this_month' },
  { label: 'Previous Month', value: 'previous_month' },
  { label: 'Custom', value: 'custom' },
];

function getAuthHeaders() {
  const sessionToken = sessionStorage.getItem('sessionToken');
  return {
    'Content-Type': 'application/json',
    ...(sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {}),
  };
}

function formatDateTime(value) {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
}

function ReferralDashboard() {
  const [preset, setPreset] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [limit] = useState(20);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [responseData, setResponseData] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});

  const isCustomPreset = preset === 'custom';

  const queryString = useMemo(() => {
    const params = new URLSearchParams({
      preset,
      limit: String(limit),
      offset: String(offset),
    });

    if (isCustomPreset) {
      if (startDate) params.append('startDate', new Date(startDate).toISOString());
      if (endDate) params.append('endDate', new Date(endDate).toISOString());
    }

    return params.toString();
  }, [preset, limit, offset, startDate, endDate, isCustomPreset]);

  const fetchReferralDashboard = useCallback(async () => {
    if (isCustomPreset && (!startDate || !endDate)) {
      setError('Start date and end date are required for custom preset.');
      return;
    }

    if (isCustomPreset && new Date(startDate) > new Date(endDate)) {
      setError('Start date must be earlier than end date.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const requestParams = new URLSearchParams(queryString);
      requestParams.set('_ts', String(Date.now()));

      const response = await fetch(`${BASE_URL}/admin/referrals/dashboard?${requestParams.toString()}`, {
        method: 'GET',
        credentials: 'include',
        headers: getAuthHeaders(),
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(result?.message || 'Failed to fetch referral dashboard.');
        setResponseData(null);
        return;
      }

      setResponseData(result?.data || null);
      setExpandedRows({});
    } catch (fetchError) {
      setError(fetchError.message || 'Failed to fetch referral dashboard.');
      setResponseData(null);
    } finally {
      setLoading(false);
    }
  }, [isCustomPreset, startDate, endDate, queryString]);

  useEffect(() => {
    fetchReferralDashboard();
  }, [fetchReferralDashboard]);

  const referralRows = responseData?.referrers || [];
  const summary = responseData?.summary || {};
  const pagination = responseData?.pagination || {};
  const timeframe = responseData?.timeframe || {};

  const hasMore = Boolean(pagination?.hasMore);

  const toggleExpanded = (referrerId) => {
    setExpandedRows((prev) => ({ ...prev, [referrerId]: !prev[referrerId] }));
  };

  const handleApplyFilters = () => {
    if (offset !== 0) {
      setOffset(0);
      return;
    }

    fetchReferralDashboard();
  };

  const handlePrevious = () => {
    const newOffset = Math.max(0, offset - limit);
    setOffset(newOffset);
  };

  const handleNext = () => {
    if (!hasMore) return;
    setOffset((prev) => prev + limit);
  };

  return (
    <Component>
      <div className="page-body">
        <div className="container-fluid">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                  <div className="d-flex align-items-center">
                    <IconButton style={{ background: '#FFFFFF' }} component={Link} to="/">
                      <ArrowBackIcon />
                    </IconButton>
                    <h3 style={{ marginLeft: '20px', marginBottom: 0 }}>Referral Dashboard</h3>
                  </div>
                  <Button component={Link} to="/admin/referrals/codes" variant="outlined" size="small">
                    Referral codes
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="container-fluid">
          <div className="row">
            <div className="col-sm-12">
              <Card>
                <CardContent>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={3}>
                      <TextField
                        label="Preset"
                        select
                        fullWidth
                        size="small"
                        value={preset}
                        onChange={(event) => setPreset(event.target.value)}
                      >
                        {PRESET_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <TextField
                        label="Start date"
                        type="datetime-local"
                        fullWidth
                        size="small"
                        disabled={!isCustomPreset}
                        value={startDate}
                        onChange={(event) => setStartDate(event.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <TextField
                        label="End date"
                        type="datetime-local"
                        fullWidth
                        size="small"
                        disabled={!isCustomPreset}
                        value={endDate}
                        onChange={(event) => setEndDate(event.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <Box sx={{ display: 'flex', gap: 1, height: '100%', alignItems: 'center' }}>
                        <Button variant="contained" onClick={handleApplyFilters} disabled={loading} fullWidth>
                          {loading ? <CircularProgress size={18} /> : 'Apply'}
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>

                  {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                      {error}
                    </Alert>
                  )}

                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={6}>
                      <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#e3f2fd' }}>
                        <Typography variant="h4" fontWeight={700}>
                          {summary.totalReferrers || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Referral values entered
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f1f8e9' }}>
                        <Typography variant="h4" fontWeight={700}>
                          {summary.totalReferrals || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Users joined with referrals
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Timeframe: {timeframe.preset || '-'} | {formatDateTime(timeframe.startDate)} to{' '}
                    {formatDateTime(timeframe.endDate)}
                  </Typography>

                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#fafafa' }}>
                          <TableCell sx={{ fontWeight: 700 }}>Referral Entered</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Matched User</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Match Type</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700 }}>
                            Users Joined
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700 }}>
                            Actions
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {!loading && referralRows.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={5} align="center">
                              <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                                No referral data found for selected timeframe.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}

                        {referralRows.map((entry, index) => {
                          const referrer = entry.referrer || {};
                          const referredUsers = entry.referredUsers || [];
                          const referralEntered = entry.enteredReferral || '-';
                          const matchedUser = referrer.id
                            ? referrer.username || referrer.name || referrer.email || referrer.phone || '-'
                            : 'No matching user';
                          const matchType = referrer.matchedBy || 'Unmatched';
                          const rowKey = `${entry.enteredReferral || 'referral'}-${referrer.id || 'unmatched'}-${index}`;
                          const expanded = !!expandedRows[rowKey];

                          return (
                            <React.Fragment key={rowKey}>
                              <TableRow hover>
                                <TableCell>{referralEntered}</TableCell>
                                <TableCell>{matchedUser}</TableCell>
                                <TableCell>{matchType}</TableCell>
                                <TableCell align="center">{entry.referredUsersCount || 0}</TableCell>
                                <TableCell align="center">
                                  <IconButton size="small" onClick={() => toggleExpanded(rowKey)} aria-label="Show referred users">
                                    {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                  </IconButton>
                                </TableCell>
                              </TableRow>

                              <TableRow>
                                <TableCell colSpan={5} sx={{ p: 0, borderBottom: 0 }}>
                                  <Collapse in={expanded} timeout="auto" unmountOnExit>
                                    <Box sx={{ m: 2 }}>
                                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                        Referred Users
                                      </Typography>
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                            <TableCell>User</TableCell>
                                            <TableCell>Email</TableCell>
                                            <TableCell>Phone</TableCell>
                                            <TableCell>Referred At</TableCell>
                                            <TableCell>Source</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {referredUsers.length === 0 ? (
                                            <TableRow>
                                              <TableCell colSpan={5} align="center">
                                                No referred users found.
                                              </TableCell>
                                            </TableRow>
                                          ) : (
                                            referredUsers.map((user) => (
                                              <TableRow key={user.id}>
                                                <TableCell>{user.username || user.name || '-'}</TableCell>
                                                <TableCell>{user.email || '-'}</TableCell>
                                                <TableCell>{user.phone || '-'}</TableCell>
                                                <TableCell>{formatDateTime(user.referredAt)}</TableCell>
                                                <TableCell>{user.source || '-'}</TableCell>
                                              </TableRow>
                                            ))
                                          )}
                                        </TableBody>
                                      </Table>
                                    </Box>
                                  </Collapse>
                                </TableCell>
                              </TableRow>
                            </React.Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                    <Button variant="outlined" onClick={handlePrevious} disabled={offset === 0 || loading}>
                      Previous
                    </Button>
                    <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                      Offset: {offset} | Limit: {limit}
                    </Typography>
                    <Button variant="outlined" onClick={handleNext} disabled={!hasMore || loading}>
                      Next
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </Component>
  );
}

export default ReferralDashboard;
